import { useEffect, useMemo, useState } from "react";
import { Check, ChevronsUpDown, Link2, ExternalLink, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
    Command,
    CommandEmpty,
    CommandGroup,
    CommandInput,
    CommandItem,
    CommandList,
} from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import adminApi, { /* unused but kept for future */ } from "@/api/adminApi";
import { Link } from "react-router-dom";

/**
 * ForeignKeyPicker
 *
 * A searchable dropdown that loads records from the parent SCRUD table and
 * lets the user pick one by its label. Emits the parent's primary-key value
 * via `onChange`.
 *
 * Props:
 *   fk       { route, table, labelField, valueField }
 *   value    current PK value (string)
 *   onChange (nextValue: string) => void
 *   disabled boolean
 */
export default function ForeignKeyPicker({ fk, value, onChange, disabled }) {
    const [open, setOpen] = useState(false);
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(false);
    const [err, setErr] = useState(null);

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        setErr(null);
        adminApi
            .list(fk.route)
            .then((data) => {
                if (cancelled) return;
                setItems(Array.isArray(data) ? data : []);
            })
            .catch((e) => {
                if (!cancelled)
                    setErr(e?.response?.data?.detail || "Failed to load options");
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [fk.route]);

    const selected = useMemo(
        () => items.find((it) => String(it[fk.valueField]) === String(value)),
        [items, fk.valueField, value]
    );

    const selectedLabel = selected
        ? `${selected[fk.labelField] ?? selected[fk.valueField]}`
        : "";

    return (
        <div className="mt-2 flex gap-2 items-start">
            <Popover open={open} onOpenChange={disabled ? () => {} : setOpen}>
                <PopoverTrigger asChild>
                    <Button
                        type="button"
                        variant="outline"
                        role="combobox"
                        aria-expanded={open}
                        disabled={disabled}
                        data-testid={`fk-trigger-${fk.route}`}
                        className="flex-1 justify-between bg-white border-navy-100 text-left h-11 font-normal hover:bg-cream"
                    >
                        <span className="flex items-center gap-2 truncate">
                            <Link2 size={14} className="text-gold-600 flex-shrink-0" />
                            {loading ? (
                                <span className="text-navy-500 inline-flex items-center gap-2">
                                    <Loader2 size={13} className="animate-spin" />
                                    Loading {fk.table}…
                                </span>
                            ) : selected ? (
                                <span className="truncate">
                                    <span className="text-navy-700 font-medium">
                                        {selectedLabel}
                                    </span>
                                    <span className="text-navy-500 text-xs ml-2 font-mono">
                                        {selected[fk.valueField]}
                                    </span>
                                </span>
                            ) : value ? (
                                <span className="text-navy-500 truncate">
                                    <span className="font-mono">{value}</span>
                                    <span className="ml-2 text-red-600 text-xs">
                                        (unknown)
                                    </span>
                                </span>
                            ) : (
                                <span className="text-navy-500">Pick a {fk.table}…</span>
                            )}
                        </span>
                        <ChevronsUpDown size={14} className="text-navy-500 flex-shrink-0" />
                    </Button>
                </PopoverTrigger>
                <PopoverContent
                    align="start"
                    className="p-0 bg-white border-navy-100 w-[--radix-popover-trigger-width] max-w-[520px]"
                >
                    <Command>
                        <CommandInput
                            placeholder={`Search ${fk.table}…`}
                            data-testid={`fk-search-${fk.route}`}
                        />
                        <CommandList>
                            {err ? (
                                <div className="p-4 text-sm text-red-600">{err}</div>
                            ) : (
                                <>
                                    <CommandEmpty>No {fk.table} match.</CommandEmpty>
                                    <CommandGroup>
                                        {value && (
                                            <CommandItem
                                                value="__clear__"
                                                onSelect={() => {
                                                    onChange("");
                                                    setOpen(false);
                                                }}
                                                className="text-navy-500 italic"
                                            >
                                                <Check size={14} className="mr-2 opacity-0" />
                                                Clear selection
                                            </CommandItem>
                                        )}
                                        {items.map((item) => {
                                            const id = String(item[fk.valueField]);
                                            const label = String(
                                                item[fk.labelField] ?? item[fk.valueField]
                                            );
                                            const isSelected =
                                                String(value ?? "") === id;
                                            return (
                                                <CommandItem
                                                    key={id}
                                                    value={`${label} ${id}`}
                                                    onSelect={() => {
                                                        onChange(id);
                                                        setOpen(false);
                                                    }}
                                                    data-testid={`fk-option-${id}`}
                                                    className="flex flex-col items-start gap-0.5"
                                                >
                                                    <div className="flex items-center gap-2 w-full">
                                                        <Check
                                                            size={14}
                                                            className={
                                                                isSelected
                                                                    ? "text-gold-600"
                                                                    : "opacity-0"
                                                            }
                                                        />
                                                        <span className="text-navy-700 font-medium truncate flex-1">
                                                            {label}
                                                        </span>
                                                    </div>
                                                    <div className="pl-6 text-[11px] font-mono text-navy-500 truncate w-full">
                                                        {id}
                                                    </div>
                                                </CommandItem>
                                            );
                                        })}
                                    </CommandGroup>
                                </>
                            )}
                        </CommandList>
                    </Command>
                </PopoverContent>
            </Popover>
            {value && (
                <Link
                    to={`/admin/data/${fk.route}`}
                    title={`Open ${fk.table} table`}
                    className="h-11 w-11 flex items-center justify-center rounded-md border border-navy-100 text-navy-500 hover:text-navy-700 hover:border-navy-700 transition-colors"
                    data-testid={`fk-open-${fk.route}`}
                >
                    <ExternalLink size={14} />
                </Link>
            )}
        </div>
    );
}
